import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import './i18n';
import App from './App';
import { initAnalytics } from '@/services/analytics';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.DEV ? 'development' : 'production',
  release: import.meta.env.VITE_APP_VERSION || 'unknown',
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
  ],
  tracesSampleRate: 0.5,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
  beforeSend(event) {
    event.tags = {
      ...event.tags,
      route: window.location.pathname,
    };
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
      const url = breadcrumb.data?.url || '';
      if (url.includes('/health') || url.includes('.js') || url.includes('.css')) {
        return null;
      }
    }
    return breadcrumb;
  },
});

if (import.meta.env.PROD) {
  const idle: (cb: () => void) => void =
    (window as any).requestIdleCallback?.bind(window) ||
    ((cb: () => void) => setTimeout(cb, 1));

  idle(async () => {
    try {
      const replayIntegration = await Sentry.lazyLoadIntegration('replayIntegration');
      Sentry.addIntegration(replayIntegration({ maskAllText: false, blockAllMedia: false }));
    } catch {}
    try {
      const feedbackIntegration = await Sentry.lazyLoadIntegration('feedbackIntegration');
      Sentry.addIntegration(feedbackIntegration({
        autoInject: true,
        colorScheme: 'dark',
        triggerLabel: 'Zgłoś problem',
        formTitle: 'Zgłoś problem',
        nameLabel: 'Imię',
        namePlaceholder: 'Twoje imię',
        emailLabel: 'Email',
        emailPlaceholder: 'twoj@email.pl',
        messageLabel: 'Opis problemu',
        messagePlaceholder: 'Co poszło nie tak? Co robiłeś/aś gdy wystąpił problem?',
        submitButtonLabel: 'Wyślij zgłoszenie',
        cancelButtonLabel: 'Anuluj',
        successMessageText: 'Dzięki za zgłoszenie! Zajmiemy się tym.',
        isNameRequired: false,
        isEmailRequired: false,
      }));
    } catch {}
  });
}

initAnalytics();

const savedTheme = localStorage.getItem('theme') || 'dark';
if (savedTheme === 'dark') {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);
